import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiClientError } from '../lib/api';
import { useSession } from '../lib/session';
import { Banner, Button, Card, CardBody, CardHeader, Field, Input, Spinner } from '../components/ui';

export function Login() {
  const { login } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      // A user with a pending forced change lands on the dashboard route, which
      // redirects them to /change-password (RequireAuth handles the gating).
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md items-center px-4">
      <div className="w-full">
        <div className="mb-8 text-center">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent-600 text-white shadow-md shadow-accent-600/20">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M5 12.5 10 17.5 19 7"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">TCG Bridge</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to submit quotes to Corrigo Pro and ServiceChannel.</p>
        </div>
        <Card>
          <CardHeader title="Sign in" subtitle="Use your team email and password" />
          <CardBody>
            <form onSubmit={onSubmit} className="space-y-4">
              {error && <Banner tone="error">{error}</Banner>}
              <Field label="Email">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </Field>
              <Field label="Password">
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </Field>
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? <Spinner /> : 'Sign in'}
              </Button>
            </form>
            <p className="mt-4 text-center text-xs text-slate-400">
              First time here, or forgot your password? Ask an admin to create your account or reset
              it — you'll get a temporary password to set your own.
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
